import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Done } from "@mui/icons-material";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";

import {
  uploadDocumentToS3,
  RecDocumentUploaded,
  activateRecDetails,
  createRecDocument,
  deActivateRecDetails,
  editRecDocumentDetails,
  getRecDocumentDetails,
} from "api";

import { useNotify, useSiteById } from "hooks";
import { definedValueOrDefault, getTextFieldColorStateProps } from "utils";
import {
  Body1SemiBold,
  CloseButton,
  UploadablePdfPreview,
  InputFieldWrapper,
  FileViewerFooter,
  RecDatePickerFormField,
} from "components";

dayjs.extend(timezone);

export const RecDetailPage: React.FunctionComponent = () => {
  // ---- Hooks & Const ----
  const { recId } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const location = useLocation();
  const receivedData = location.state;
  const query = new URLSearchParams(useLocation().search);
  const siteId = query.get("s");

  // ---- API's ----
  const { data: site } = useSiteById(Number(siteId));
  const { data: recData, mutateAsync: getRecDocument } = useMutation(
    getRecDocumentDetails,
  );
  const { mutateAsync: activateRec } = useMutation(activateRecDetails);
  const { mutateAsync: deActivateRec } = useMutation(deActivateRecDetails);
  const { mutateAsync: editRec } = useMutation(editRecDocumentDetails);
  const { mutateAsync: markUploaded } = useMutation(RecDocumentUploaded);
  const { mutate: createDoc } = useMutation(createRecDocument);
  const { mutateAsync: uploadToS3 } = useMutation(uploadDocumentToS3);

  // ---- States ----
  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const [disableBtn, setDisableBtn] = useState<boolean>(true);
  const [scale, setScale] = useState<number>(0.75);

  const recDocumentDetails = recData !== undefined && recData?.data;

  const defaultValues = useMemo(
    () => ({
      utilityProvider: (recDocumentDetails as any)?.utilityProvider,
      utilityType: recData?.data.utilityTypeName,
      renewableEnergy:
        recData !== undefined &&
        definedValueOrDefault(recData?.data.renewableEnergy, ""),
      startDate: dayjs(recData?.data.startDate, "YYYY-MM-DD"),
      endDate: dayjs(recData?.data.endDate, "YYYY-MM-DD"),
      country:
        recData !== undefined &&
        definedValueOrDefault(recData?.data.country, ""),
    }),
    [recDocumentDetails, recData],
  );

  const { control, formState, reset, getValues } = useForm<any>({
    defaultValues,
    mode: "onChange",
  });

  const handleSaveBtnClick = () => {
    const selectedIds: any = [];
    const deselectedIds: any = [];

    (recDocumentDetails as any)?.collectorRecsDtoList?.forEach((t: any) => {
      if (selectedOptions.includes(t.collectorId)) {
        selectedIds.push(t.collectorId);
      } else {
        deselectedIds.push(t.collectorId);
      }
    });

    deActivateRec({
      recId: parseInt(recId!, 10),
      collectorIds: deselectedIds,
    });

    activateRec(
      {
        recId: parseInt(recId!, 10),
        collectorIds: selectedIds,
      },
      {
        onSuccess: () => {
          // notify.success(`Rec site is Updated !`);
        },
      },
    );

    const endDate =
      getValues("endDate") === "Invalid Date" || getValues("endDate") === null
        ? null
        : new Date(getValues("endDate"));

    const startDate =
      getValues("startDate") === "Invalid Date" ||
      getValues("startDate") === null
        ? null
        : new Date(getValues("startDate"));

    const percentage = getValues("renewableEnergy");
    const utilityType = getValues("utilityType");

    editRec(
      {
        recId,
        payload: {
          endDate:
            dayjs(endDate).format("YYYY-MM-DDTHH:mm:ss[Z]") === "Invalid Date"
              ? null
              : dayjs(endDate).format("YYYY-MM-DDT00:00:00[Z]"),
          startDate:
            dayjs(startDate).format("YYYY-MM-DDTHH:mm:ss[Z]") === "Invalid Date"
              ? null
              : dayjs(startDate).format("YYYY-MM-DDT00:00:00[Z]"),
          renewableEnergy: percentage,
          utilityType,
        },
      },
      {
        onSuccess: () => {
          getRecDocument(recId);
          notify.success("RECs have been updated");
          navigate(-1);
        },
      },
    );
  };

  const handleChange = (event: any) => {
    const selectedItems = event.target.value;
    setSelectedOptions(selectedItems);
  };

  // Function to get the ID of an option
  const getOptionId = (value: any) => {
    const parts = value.split(" - ");
    return parts[parts.length - 1];
  };

  const handleFileSelected = (file: File) => {
    createDoc(
      { fileName: file.name, recId: parseInt(recId!, 10) },
      {
        onSuccess: (details) => {
          uploadToS3({ url: details.s3uploadUrl, file })
            .then(() => markUploaded({ recId: parseInt(recId!, 10) }))
            .then(() => {
              notify.success(
                "Successfully uploaded Utility Provider Renewable Energy Rate file!",
              );
              getRecDocument(recId);
            });
        },
      },
    );
  };

  const handleFileReUploaded = (status: any) => {
    if (status === true) {
      getRecDocument(recId);
    }
  };

  useEffect(() => {
    const statusArr =
      recDocumentDetails &&
      recDocumentDetails?.collectorRecsDtoList
        .filter((t: any) => t.recsActive === true)
        .map((item: any) => item.collectorId);
    setSelectedOptions(statusArr !== false ? statusArr : []);
  }, [recDocumentDetails]);

  useEffect(() => {
    reset(defaultValues);
  }, [reset, recData, defaultValues]);

  useEffect(() => {
    if (getValues("startDate") === null) {
      setDisableBtn(true);
    } else {
      setDisableBtn(false);
    }
  }, [getValues("startDate")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getRecDocument(recId);
  }, [recId, getRecDocument]);

  return (
    <>
      {recData !== undefined && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              px: 3,
              flexGrow: 1,
              flexDirection: "row",
              overflow: "auto",
            }}
          >
            {/* Section 1 */}
            <UploadablePdfPreview
              downloadUrl={(recDocumentDetails as any)?.downloadUrl}
              scale={scale}
              onFileSelected={handleFileSelected}
              withHeader={117}
            />
            {/* Section 2 */}
            <Box
              sx={{
                ml: 4,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box component="section" mb={3} mt={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Body1SemiBold mb={3}>
                    Utility Provider Renewable Energy Rate Details
                  </Body1SemiBold>
                  <CloseButton
                    onClick={() => navigate(-1)}
                    variant="outlined"
                  />
                </Box>

                <Stack direction="row" mb={2} spacing={3}>
                  <InputFieldWrapper label="Utility Provider">
                    <Controller
                      name="utilityProvider"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          disabled
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                  <InputFieldWrapper label="Utility Type">
                    <Controller
                      name="utilityType"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          disabled
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Stack>
                <Stack direction="row" mb={2} spacing={3}>
                  <InputFieldWrapper label="Country">
                    <Controller
                      name="country"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          disabled
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                  <InputFieldWrapper label="Coverage Percentage">
                    <Controller
                      name="renewableEnergy"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                          disabled={!site?.enabled}
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Stack>

                <Stack direction="row" mb={2} spacing={3}>
                  <RecDatePickerFormField
                    name="startDate"
                    label="Start Date"
                    control={control}
                    disabled={false || !site?.enabled}
                    rules={{
                      required: receivedData?.startDate,
                    }}
                    {...getTextFieldColorStateProps(
                      "startDate",
                      formState.errors,
                    )}
                  />

                  <RecDatePickerFormField
                    name="endDate"
                    label="End Date (Optional)"
                    control={control}
                    disabled={false || !site?.enabled}
                    rules={{
                      required: receivedData?.endDate,
                    }}
                    {...getTextFieldColorStateProps(
                      "endDate",
                      formState.errors,
                    )}
                  />
                </Stack>

                <Stack direction="row" spacing={3}>
                  <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
                    <InputFieldWrapper label="Apply to other accounts">
                      <Select
                        multiple
                        value={selectedOptions}
                        onChange={handleChange}
                        autoWidth
                        fullWidth
                        disabled={!site?.enabled}
                        sx={{ height: 40 }}
                        input={<OutlinedInput />}
                        renderValue={(selected) => (
                          <div>
                            <MenuItem>{selected.length} Selected</MenuItem>
                          </div>
                        )}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                        }}
                      >
                        {(recDocumentDetails as any)?.collectorRecsDtoList.map(
                          (option: any) => {
                            const itemId = getOptionId(
                              `${option.siteName} - ${option.provider} - ${option.collectorId}`,
                            );
                            const isSelected = selectedOptions.includes(
                              option.collectorId,
                            );

                            const siteName =
                              option.siteName !== null
                                ? `${option.siteName}`
                                : "";
                            const accountNumber =
                              option.accountNumber !== null
                                ? `${option.accountNumber}`
                                : "";
                            const meterNumber =
                              option.meterNumber !== null
                                ? `${option.meterNumber}`
                                : "";

                            const utilityType = getValues("utilityType");

                            return (
                              <MenuItem
                                key={itemId}
                                value={option.collectorId}
                                selected={isSelected}
                                sx={{
                                  width: "100%",
                                  "& > div: first-of-type": {
                                    height: "26px",
                                  },
                                }}
                              >
                                <ListItemText
                                  secondary={`${siteName} - ${accountNumber} - ${meterNumber} - ${utilityType}`}
                                />
                                {isSelected ? (
                                  <Done style={{ marginLeft: "12px" }} />
                                ) : null}
                              </MenuItem>
                            );
                          },
                        )}
                      </Select>
                    </InputFieldWrapper>
                  </FormControl>
                </Stack>
              </Box>

              <Box
                sx={{
                  mt: 4,
                  lexGrow: 1,
                  overflow: "hidden",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button onClick={() => navigate(-1)}>Cancel</Button>
                <Button
                  onClick={() => handleSaveBtnClick()}
                  variant="contained"
                  disabled={disableBtn || !site?.enabled}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Box>

          <FileViewerFooter
            fileName={(recDocumentDetails as any)?.recsFileName}
            setScale={setScale}
            downloadUrl={(recDocumentDetails as any)?.downloadUrl}
            actionsDisabled={false}
            isFileUploaded={handleFileReUploaded}
            successMessage="Successfully uploaded Utility Provider Renewable Energy Rate file !"
            deleteTarget="Utility Provider Renewable Energy Rate"
            deleteTitle="Delete Utility Provider Renewable Energy Rate documents?"
          />
        </Box>
      )}
    </>
  );
};
